import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Chip, Grid, TableRow, TableCell,
  DialogContent, DialogActions, TextField, Alert, Tooltip,
  FormControl, InputLabel, Select, MenuItem, TablePagination,
  Divider, CircularProgress, useTheme
} from '@mui/material';
import {
  CheckCircle, Cancel, Refresh, Visibility, AccountBalance,
  Payment, Schedule, Done, Close, Search
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  SectionHeader, GlassPaper, GlassTable, GlassDialog, GlassDialogSection,
  GradientButton, StatCard, EmptyState, ShimmerTableRows
} from './ui';
import { glass } from './ui/glassTokens';

function WithdrawalManagement() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [summary, setSummary] = useState({
    pending: { count: 0, amount: 0 },
    processing: { count: 0, amount: 0 },
    completed: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [transactionRef, setTransactionRef] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
  }, [page, rowsPerPage, statusFilter, methodFilter]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });
      if (statusFilter) params.append('status', statusFilter);
      if (methodFilter) params.append('method', methodFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get(`/admin/withdrawals?${params}`);
      setWithdrawals(response.data.data.withdrawals || []);
      setTotalCount(response.data.data.pagination?.total || 0);
      setSummary(response.data.data.summary || {});
    } catch (err) {
      setError('Failed to load withdrawals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchWithdrawals();
  };

  const handleApprove = async (withdrawal) => {
    try {
      setProcessing(true);
      await api.put(`/admin/withdrawals/${withdrawal.id}/approve`);
      setSuccess('Withdrawal approved and marked as processing');
      fetchWithdrawals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    try {
      setProcessing(true);
      await api.put(`/admin/withdrawals/${selectedWithdrawal.id}/reject`, {
        reason: rejectReason,
      });
      setSuccess('Withdrawal rejected and amount refunded');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedWithdrawal(null);
      fetchWithdrawals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const handleComplete = async () => {
    try {
      setProcessing(true);
      await api.put(`/admin/withdrawals/${selectedWithdrawal.id}/complete`, {
        transaction_reference: transactionRef || null,
      });
      setSuccess('Withdrawal marked as completed');
      setCompleteDialogOpen(false);
      setTransactionRef('');
      setSelectedWithdrawal(null);
      fetchWithdrawals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete withdrawal');
    } finally {
      setProcessing(false);
    }
  };

  const statusColor = (status) =>
    status === 'completed' ? '#10B981' :
    status === 'pending' ? '#F59E0B' :
    status === 'processing' ? '#22D3EE' : '#F43F5E';

  const statusChipSx = (status) => ({
    fontSize: '0.7rem',
    fontWeight: 600,
    height: 22,
    background: status === 'completed' ? 'rgba(16,185,129,0.15)' :
      status === 'pending' ? 'rgba(245,158,11,0.15)' :
      status === 'processing' ? 'rgba(34,211,238,0.15)' :
      'rgba(244,63,94,0.15)',
    color: statusColor(status),
    border: 'none',
  });

  const getStatusChip = (status) => {
    const icons = {
      pending: <Schedule sx={{ fontSize: '0.75rem !important' }} />,
      processing: <Payment sx={{ fontSize: '0.75rem !important' }} />,
      completed: <Done sx={{ fontSize: '0.75rem !important' }} />,
      rejected: <Close sx={{ fontSize: '0.75rem !important' }} />,
    };
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        size="small"
        icon={icons[status]}
        sx={statusChipSx(status)}
      />
    );
  };

  const getMethodChip = (method) => (
    <Chip
      label={method.toUpperCase()}
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.7rem',
        height: 22,
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,23,42,0.15)',
        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)',
      }}
    />
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const selectSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
    '& .MuiSelect-select': {
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiSvgIcon-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const paginationSx = {
    color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    '& .MuiSelect-select': { color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' },
    '& .MuiSvgIcon-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
    '& .MuiButtonBase-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
  };

  return (
    <Box>
      <SectionHeader
        eyebrow="Finance"
        title="Withdrawal Management"
        action={
          <GradientButton variant="ghost" startIcon={<Refresh />} onClick={fetchWithdrawals} disabled={loading}>
            Refresh
          </GradientButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Summary KPIs */}
      <Grid container spacing={3} mb={4}>
        {[
          { key: 'pending', label: 'Pending', icon: <Schedule />, iconIndex: 3 },
          { key: 'processing', label: 'Processing', icon: <Payment />, iconIndex: 1 },
          { key: 'completed', label: 'Completed', icon: <Done />, iconIndex: 2 },
          { key: 'rejected', label: 'Rejected', icon: <Close />, iconIndex: 0 },
        ].map(({ key, label, icon, iconIndex }, idx) => (
          <Grid item xs={6} sm={3} key={key}>
            <StatCard
              label={label}
              value={summary[key]?.count || 0}
              suffix={` / ₹${((summary[key]?.amount || 0) / 100).toFixed(0)}`}
              icon={icon}
              iconIndex={iconIndex}
              index={idx}
            />
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <GlassPaper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Search by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={handleSearch} sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}>
                    <Search fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small" sx={selectSx}>
              <InputLabel>Method</InputLabel>
              <Select
                value={methodFilter}
                label="Method"
                onChange={(e) => {
                  setMethodFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="bank">Bank Transfer</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <GradientButton
              variant="ghost"
              fullWidth
              onClick={() => {
                setStatusFilter('');
                setMethodFilter('');
                setSearchQuery('');
                setPage(0);
              }}
            >
              Clear
            </GradientButton>
          </Grid>
        </Grid>
      </GlassPaper>

      {/* Withdrawals Table */}
      <GlassTable
        columns={[
          { label: 'User' },
          { label: 'Amount' },
          { label: 'Method' },
          { label: 'Account Details' },
          { label: 'Status' },
          { label: 'Requested' },
          { label: 'Actions', align: 'center' },
        ]}
      >
        {loading ? (
          <ShimmerTableRows count={5} cols={7} />
        ) : withdrawals.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<AccountBalance />}
                iconIndex={0}
                title="No withdrawal requests found"
                description="Withdrawal requests from librarians will appear here."
              />
            </TableCell>
          </TableRow>
        ) : (
          withdrawals.map((withdrawal) => (
            <TableRow key={withdrawal.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                  {withdrawal.user?.name || 'N/A'}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                  {withdrawal.user?.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="700" sx={{ color: '#6366F1' }}>
                  {withdrawal.amount_display}
                </Typography>
              </TableCell>
              <TableCell>{getMethodChip(withdrawal.method)}</TableCell>
              <TableCell>
                {withdrawal.method === 'upi' ? (
                  <Typography variant="body2">{withdrawal.upi_id}</Typography>
                ) : (
                  <Box>
                    <Typography variant="body2">{withdrawal.bank_account_name}</Typography>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                      ****{withdrawal.bank_account_number?.slice(-4)} | {withdrawal.bank_ifsc}
                    </Typography>
                  </Box>
                )}
              </TableCell>
              <TableCell>{getStatusChip(withdrawal.status)}</TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                {formatDate(withdrawal.created_at)}
              </TableCell>
              <TableCell align="center">
                <Box display="flex" gap={0.5} justifyContent="center">
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedWithdrawal(withdrawal);
                        setDetailsDialogOpen(true);
                      }}
                      sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {withdrawal.status === 'pending' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton
                          size="small"
                          onClick={() => handleApprove(withdrawal)}
                          disabled={processing}
                          sx={{ color: '#10B981' }}
                        >
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedWithdrawal(withdrawal);
                            setRejectDialogOpen(true);
                          }}
                          sx={{ color: '#F43F5E' }}
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {withdrawal.status === 'processing' && (
                    <>
                      <Tooltip title="Mark as Completed">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedWithdrawal(withdrawal);
                            setCompleteDialogOpen(true);
                          }}
                          sx={{ color: '#10B981' }}
                        >
                          <Done fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedWithdrawal(withdrawal);
                            setRejectDialogOpen(true);
                          }}
                          sx={{ color: '#F43F5E' }}
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={paginationSx}
      />

      {/* Details Dialog */}
      <GlassDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        title="Withdrawal Details"
        maxWidth="sm"
        fullWidth
        titleAction={selectedWithdrawal && getStatusChip(selectedWithdrawal.status)}
      >
        <DialogContent sx={{ pt: 2 }}>
          {selectedWithdrawal && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>User</Typography>
                  <Typography variant="body1" fontWeight="600">{selectedWithdrawal.user?.name}</Typography>
                  <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                    {selectedWithdrawal.user?.email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Amount</Typography>
                  <Typography variant="h5" fontWeight="700" sx={{ color: '#6366F1' }}>
                    {selectedWithdrawal.amount_display}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.07)' }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Method</Typography>
                  <Typography variant="body1">{selectedWithdrawal.method.toUpperCase()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Requested On</Typography>
                  <Typography variant="body1">{formatDate(selectedWithdrawal.created_at)}</Typography>
                </Grid>
                {selectedWithdrawal.method === 'upi' ? (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>UPI ID</Typography>
                    <Typography variant="body1" fontWeight="600">{selectedWithdrawal.upi_id}</Typography>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Account Holder</Typography>
                      <Typography variant="body1">{selectedWithdrawal.bank_account_name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Account Number</Typography>
                      <Typography variant="body1" fontWeight="600">{selectedWithdrawal.bank_account_number}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>IFSC Code</Typography>
                      <Typography variant="body1">{selectedWithdrawal.bank_ifsc}</Typography>
                    </Grid>
                  </>
                )}
                {selectedWithdrawal.transaction_reference && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Transaction Reference</Typography>
                    <Typography variant="body1" fontWeight="600">{selectedWithdrawal.transaction_reference}</Typography>
                  </Grid>
                )}
                {selectedWithdrawal.rejection_reason && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      <Typography variant="subtitle2">Rejection Reason:</Typography>
                      {selectedWithdrawal.rejection_reason}
                    </Alert>
                  </Grid>
                )}
                {selectedWithdrawal.processed_at && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>Processed On</Typography>
                    <Typography variant="body1">{formatDate(selectedWithdrawal.processed_at)}</Typography>
                    {selectedWithdrawal.processor && (
                      <Typography variant="caption" sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}>
                        by {selectedWithdrawal.processor.name}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <GradientButton variant="ghost" onClick={() => setDetailsDialogOpen(false)}>Close</GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* Reject Dialog */}
      <GlassDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        title="Reject Withdrawal"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Rejecting this withdrawal will refund {selectedWithdrawal?.amount_display} back to the user's wallet.
          </Alert>
          <TextField
            fullWidth
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            sx={{
              '& .MuiOutlinedInput-root': {
                ...g.surface,
                '& fieldset': { border: 'none' },
                color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
              },
              '& .MuiInputLabel-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={() => setRejectDialogOpen(false)} disabled={processing}>
            Cancel
          </GradientButton>
          <GradientButton
            variant="danger"
            onClick={handleReject}
            disabled={processing || !rejectReason.trim()}
          >
            {processing ? 'Rejecting...' : 'Reject & Refund'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* Complete Dialog */}
      <GlassDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        title="Mark Withdrawal as Completed"
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Please ensure you have transferred {selectedWithdrawal?.amount_display} to the user before marking as completed.
          </Alert>
          <TextField
            fullWidth
            label="Transaction Reference (Optional)"
            value={transactionRef}
            onChange={(e) => setTransactionRef(e.target.value)}
            placeholder="Bank/UPI transaction reference number"
            helperText="Enter the bank or UPI transaction reference for record"
            sx={{
              '& .MuiOutlinedInput-root': {
                ...g.surface,
                '& fieldset': { border: 'none' },
                color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
              },
              '& .MuiInputLabel-root': { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' },
              '& .MuiFormHelperText-root': { color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={() => setCompleteDialogOpen(false)} disabled={processing}>
            Cancel
          </GradientButton>
          <GradientButton
            variant="primary"
            onClick={handleComplete}
            disabled={processing}
          >
            {processing ? 'Processing...' : 'Mark as Completed'}
          </GradientButton>
        </DialogActions>
      </GlassDialog>
    </Box>
  );
}

export default WithdrawalManagement;
